import { useEffect } from 'react';
import './MakeItRainAnimation.css';

/**
 * PROFESSIONAL MAKE IT RAIN ANIMATION
 * Steel-type special move - hurls coins at the opponent
 * 
 * Key Features:
 * - Steel-type special attack (120 base power, 100% accuracy)
 * - Lowers user's Sp. Atk by 1 stage
 * - Gholdengo signature move with golden coin shower
 * - Professional gaming quality with metallic coin effects
 */
export default function MakeItRainAnimation({ playerRef, opponentRef, onComplete }) {
  useEffect(() => {
    const playerEl = playerRef.current;
    const opponentEl = opponentRef.current;
    if (!playerEl || !opponentEl) return;

    const container = playerEl.closest('.battle-screen') || playerEl.parentElement || document.body;
    const containerRect = container.getBoundingClientRect();

    const playerRect = playerEl.getBoundingClientRect();
    const opponentRect = opponentEl.getBoundingClientRect();

    const playerX = Math.round((playerRect.left + playerRect.width / 2) - containerRect.left);
    const playerY = Math.round((playerRect.top + playerRect.height / 2) - containerRect.top);
    const opponentX = Math.round((opponentRect.left + opponentRect.width / 2) - containerRect.left);
    const opponentY = Math.round((opponentRect.top + opponentRect.height / 2) - containerRect.top);

    const dx = opponentX - playerX;
    const dy = opponentY - playerY;

    const elements = [];

    // PHASE 1: Golden Energy Charge (0-500ms)
    playerEl.classList.add('make-it-rain-user-charge');

    const goldAura = document.createElement('div');
    goldAura.className = 'make-it-rain-gold-aura';
    goldAura.style.left = `${playerX - 70}px`;
    goldAura.style.top = `${playerY - 70}px`;
    container.appendChild(goldAura);
    elements.push(goldAura);

    // Gold sparkles around player
    for (let i = 0; i < 10; i++) {
      const sparkle = document.createElement('div');
      sparkle.className = 'make-it-rain-sparkle';
      const angle = (i / 10) * 360;
      const radius = 55;
      sparkle.style.left = `${playerX + Math.cos(angle * Math.PI / 180) * radius - 4}px`;
      sparkle.style.top = `${playerY + Math.sin(angle * Math.PI / 180) * radius - 4}px`;
      sparkle.style.animationDelay = `${i * 40}ms`;
      container.appendChild(sparkle);
      elements.push(sparkle);
    }

    // PHASE 2: Coin Barrage (500-1100ms)
    const phase2Timer = setTimeout(() => {
      // Multiple coin projectiles
      for (let i = 0; i < 12; i++) {
        const coinTimer = setTimeout(() => {
          const coin = document.createElement('div');
          coin.className = 'make-it-rain-coin';
          const offsetX = (Math.random() - 0.5) * 40;
          const offsetY = (Math.random() - 0.5) * 30;
          coin.style.left = `${playerX + offsetX}px`;
          coin.style.top = `${playerY + offsetY}px`;
          coin.style.setProperty('--dx', `${dx - offsetX}px`);
          coin.style.setProperty('--dy', `${dy - offsetY}px`);
          coin.style.animationDelay = `${i * 15}ms`;
          container.appendChild(coin);
          elements.push(coin);
        }, i * 35);
        elements.push({ timer: coinTimer, type: 'timer' });
      }

      // Gold trail particles
      for (let i = 0; i < 14; i++) {
        const trailTimer = setTimeout(() => {
          const trail = document.createElement('div');
          trail.className = 'make-it-rain-trail';
          const progress = i / 14;
          trail.style.left = `${playerX + dx * progress + (Math.random() - 0.5) * 30 - 4}px`;
          trail.style.top = `${playerY + dy * progress + (Math.random() - 0.5) * 30 - 4}px`;
          container.appendChild(trail);
          elements.push(trail);
        }, i * 35);
        elements.push({ timer: trailTimer, type: 'timer' });
      }
    }, 500);
    elements.push({ timer: phase2Timer, type: 'timer' });

    // PHASE 3: Golden Impact (1100-1600ms)
    const phase3Timer = setTimeout(() => {
      opponentEl.classList.add('make-it-rain-opponent-hit');

      // Gold explosion
      const explosion = document.createElement('div');
      explosion.className = 'make-it-rain-explosion';
      explosion.style.left = `${opponentX - 100}px`;
      explosion.style.top = `${opponentY - 100}px`;
      container.appendChild(explosion);
      elements.push(explosion);

      // Coin burst
      for (let i = 0; i < 20; i++) {
        const burstAngle = (i / 20) * 360;
        const burstDist = 50 + Math.random() * 50;
        const burst = document.createElement('div');
        burst.className = 'make-it-rain-coin-burst';
        burst.style.left = `${opponentX - 5}px`;
        burst.style.top = `${opponentY - 5}px`;
        burst.style.setProperty('--scatter-x', `${Math.cos(burstAngle * Math.PI / 180) * burstDist}px`);
        burst.style.setProperty('--scatter-y', `${Math.sin(burstAngle * Math.PI / 180) * burstDist}px`);
        burst.style.animationDelay = `${i * 12}ms`;
        container.appendChild(burst);
        elements.push(burst);
      }

      for (let i = 0; i < 3; i++) {
        const swTimer = setTimeout(() => {
          const sw = document.createElement('div');
          sw.className = 'make-it-rain-shockwave';
          sw.style.left = `${opponentX - 65}px`;
          sw.style.top = `${opponentY - 65}px`;
          container.appendChild(sw);
          elements.push(sw);
        }, i * 100);
        elements.push({ timer: swTimer, type: 'timer' });
      }

      // Raining coins falling down around impact
      for (let i = 0; i < 10; i++) {
        const rainTimer = setTimeout(() => {
          const rain = document.createElement('div');
          rain.className = 'make-it-rain-falling-coin';
          rain.style.left = `${opponentX + (Math.random() - 0.5) * 120 - 5}px`;
          rain.style.top = `${opponentY - 60 - Math.random() * 30}px`;
          container.appendChild(rain);
          elements.push(rain);
        }, i * 30);
        elements.push({ timer: rainTimer, type: 'timer' });
      }
    }, 1100);
    elements.push({ timer: phase3Timer, type: 'timer' });

    // CLEANUP (1800ms)
    const cleanupTimer = setTimeout(() => {
      playerEl.classList.remove('make-it-rain-user-charge');
      opponentEl.classList.remove('make-it-rain-opponent-hit');
      elements.forEach(item => {
        if (item.type === 'timer') clearTimeout(item.timer);
        else if (item && item.parentNode) item.remove();
      });
      onComplete();
    }, 1800);

    return () => {
      clearTimeout(cleanupTimer);
      playerEl.classList.remove('make-it-rain-user-charge');
      opponentEl.classList.remove('make-it-rain-opponent-hit');
      elements.forEach(item => {
        if (item.type === 'timer') clearTimeout(item.timer);
        else if (item && item.parentNode) item.remove();
      });
    };
  }, [playerRef, opponentRef, onComplete]);

  return null;
}

MakeItRainAnimation.moveType = 'steel';
